import { q, z } from 'groqd'
import type { Selection } from 'groqd'

import type { Language } from '@nxt-insurance/constants'

export const imageAsset = {
  url: q.string(),
  _id: q.string(),
  extension: q.string(),
  size: q.number(),
  uploadId: q.string().optional(),
  metadata: q.object({
    lqip: q.string(),
    dimensions: q.object({
      aspectRatio: q.number(),
      height: q.number(),
      width: q.number(),
    }),
  }),
} satisfies Selection

export const imageSchema = z.object({
  _type: z.literal('image'),
  alt: z.string().optional(),
  aspectRatio: z
    .object({
      mobile: z.number().optional(),
      desktop: z.number().optional(),
    })
    .optional(),
  asset: z.union([
    z.object({
      _type: z.literal('reference'),
      _ref: z.string(),
    }),
    z.object({
      _id: z.string(),
      url: z.string(),
      extension: z.string(),
      size: z.number(),
      uploadId: z.string().optional(),
      metadata: z.object({
        lqip: z.string(),
        dimensions: z.object({
          aspectRatio: z.number(),
          height: z.number(),
          width: z.number(),
        }),
      }),
    }),
  ]),
  crop: z
    .object({
      top: z.number(),
      bottom: z.number(),
      left: z.number(),
      right: z.number(),
    })
    .optional(),
  hotspot: z
    .object({
      x: z.number(),
      y: z.number(),
      height: z.number(),
      width: z.number(),
    })
    .optional(),
})

export const illustrationQuery = q('*')
  .filter('_type == "illustration" && _id == $id')
  .grab({
    _type: q.literal('illustration'),
    imageFiles: q('imageFiles')
      .filter()
      .grab({
        image: q('image').grab({
          _type: q.literal('image'),
          asset: q('asset').deref().grab(imageAsset),
        }),
        language: q.string(),
        market: q.string(),
      })
      .nullable(),
  })
  .slice(0)

export const imageAssetQuery = q('*')
  .filter('_type == "sanity.imageAsset" && _id == $id')
  .grab(imageAsset)
  .slice(0)

export const getFlexibleImageSchema = (lang: Language) =>
  z.object({
    _type: z.literal('flexibleImage'),
    alt: z
      .union([
        z.object({
          [lang]: z.string().optional(),
        }),
        z.string(),
      ])
      .nullable()
      .optional(),
    imageWidth: z.number().nullable().optional(),
    desktopImage: z
      .array(
        z.discriminatedUnion('_type', [
          imageSchema,
          z.object({
            _type: z.literal('illustration'),
            imageFiles: z
              .array(
                z.object({
                  image: z.object({
                    _type: z.literal('image'),
                    asset: z.object({
                      _id: z.string(),
                      url: z.string(),
                      extension: z.string(),
                      size: z.number(),
                      uploadId: z.string().optional(),
                      metadata: z.object({
                        lqip: z.string(),
                        dimensions: z.object({
                          aspectRatio: z.number(),
                          height: z.number(),
                          width: z.number(),
                        }),
                      }),
                    }),
                  }),
                  language: z.string(),
                  market: z.string(),
                })
              )
              .nullable(),
          }),
          z.object({
            _type: z.literal('reference'),
            _ref: z.string(),
          }),
        ])
      )
      .max(1)
      .optional(),
  })

export type FlexibleImage = z.infer<ReturnType<typeof getFlexibleImageSchema>>
